import { Component, computed, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { DatePickerEnum, NgxDateRangePickerOutput, parseDateFormat } from 'ngx-date-range-picker';
import { NgxDropdownComponent, NgxDropdownOption } from 'ngx-dropdown';
import { auditTime, combineLatest, distinctUntilChanged, filter, map, Observable, tap } from 'rxjs';
import { RegConfig } from '../../shared/models';
import { DataViewModelService } from '../../shared/services/data-vm.service';
import { InternalService } from '../../shared/services/internal.service';
import { MixPanelService } from '../../shared/services/mixpanel.service';
import { AppState } from '../../store';
import * as Actions from '../../store/actions/com.actions';
import * as Selectors from '../../store/selectors/com.selectors';
import { FiltersStateService } from './filters-state.service';

@Component({
  selector: 'dr-customer-offers-ui-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss']
})
export class FiltersComponent implements OnInit {
  private regFirstTimeSelected = false;
  private periodSelected = false;
  private isFirstTime = true;

  @ViewChild('ngx_program_dropdown', { static: true })
  ngxProgramDropdown!: NgxDropdownComponent;

  @ViewChild('ngx_reg_dropdown', { static: true })
  ngxRegDropdown!: NgxDropdownComponent;

  @ViewChild('ngx_drp') dateRangePicker!: NgxDateRangePickerOutput;

  // Form controls
  protected selectedFromWeekPickerFormControl = this.dataVMService.selectedFromWeekPickerFormControl;
  protected selectedFromDayPickerFormControl = this.dataVMService.selectedFromDayPickerFormControl;
  protected selectedProgramFormControl = this.dataVMService.selectedProgramFormControl;
  protected selectedRegFormControl = this.dataVMService.selectedRegFormControl;

  protected isScheduleTab$: Observable<boolean> = this.route.events.pipe(
    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    map((e: NavigationEnd) => !e.urlAfterRedirects.includes('schedule'))
  );

  // NgRx Observables
  public programsData$ = this.store.select(Selectors.selectAllPrograms);
  public programsLoading$ = this.store.select(Selectors.selectProgramsRequest).pipe(map((r) => r.loading));
  public selectedProgram$ = this.store.select(Selectors.selectSelectedProgram);
  public registrationsData$ = this.store.select(Selectors.selectAllRegistrations);
  public selectedRegistration$ = this.store.select(Selectors.selectSelectedRegistration);
  public regViewConfig$ = this.store.select(Selectors.selectCurrentRegViewConfig);

  private currentRouteSignal = toSignal(
    this.route.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e: NavigationEnd) => e.urlAfterRedirects)
    ),
    { initialValue: this.route.url }
  );

  protected isChartRouteSignal = computed(() => {
    const currentRoute = this.currentRouteSignal();
    return currentRoute.includes('/chart');
  });

  // Date picker type from service
  private lastRegConfigRegId: string | null = null;
  protected datePickerType = this.filtersStateService.datePickerType;
  protected datePickerEnum = DatePickerEnum;
  protected DATE_PICKER_INITIAL_DAY_OFFSET = 2;
  public applyButtonDisabled = this.filtersStateService.isApplyButtonDisabledSignal;

  protected userLocale = computed(() => this.internalState.userPreferenceSignal()?.locale ?? 'en-US');
  protected datePickerHint = computed(() => {
    const format = moment().locale(this.userLocale()).localeData().longDateFormat('L') ?? 'MM/DD/YYYY';
    return `${format} - ${format}`;
  });
  protected datePickerFormat = computed(() => parseDateFormat(moment().locale(this.userLocale()).localeData().longDateFormat('L') ?? 'MM/DD/YYYY'));
  constructor(
    private internalState: InternalService,
    private dataVMService: DataViewModelService,
    private mixPanelService: MixPanelService,
    private route: Router,
    private destroyRef: DestroyRef,
    private store: Store<AppState>,
    private filtersStateService: FiltersStateService
  ) {}

  ngOnInit(): void {
    // Dispatch action to load programs
    this.store.dispatch(Actions.loadPrograms());

    // Track loading states
    combineLatest([this.store.select(Selectors.selectRegistrationsLoading), this.store.select(Selectors.selectRegViewConfigLoading)])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([regLoading, configLoading]) => {
        this.filtersStateService.setLoading(regLoading || configLoading);
      });

    combineLatest([
      this.regViewConfig$,
      this.route.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e: NavigationEnd) => e.urlAfterRedirects)
      )
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(([regConfig, _]) => regConfig !== null)
      )
      .subscribe(([regConfig, currentRoute]) => {
        this.onRegConfigOrRouteChange(regConfig as RegConfig, currentRoute);
      });

    this.selectedProgram$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((program: NgxDropdownOption | null) => {
          this.selectedProgramFormControl.setValue(program, { emitEvent: false });

          if (program) {
            // Cancel any in-progress registration loading
            this.store.dispatch(Actions.cancelLoadRegistrations());

            // Mark as automatic selection
            this.filtersStateService.setAutomaticSelection(true);

            // Load registrations by program
            this.store.dispatch(Actions.loadRegistrations({ programId: program.id }));
          } else {
            this.store.dispatch(Actions.loadRegistrationsUsingSession());
          }
        })
      )
      .subscribe();

    this.selectedRegistration$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((reg: NgxDropdownOption | null) => {
          if (reg) {
            this.selectedRegFormControl.setValue(reg, { emitEvent: true });
          } else {
            this.selectedRegFormControl.setValue(null, { emitEvent: false });
          }
        })
      )
      .subscribe();

    this.selectedProgramFormControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((program: NgxDropdownOption | null) => {
          if (!program) return;

          this.isFirstTime = true;
          this.filtersStateService.setApplyButtonChanging();
          this.filtersStateService.setAutomaticSelection(true);
          this.store.dispatch(Actions.selectProgram({ program }));
        })
      )
      .subscribe();

    this.selectedRegFormControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        auditTime(0),
        filter((reg): reg is NgxDropdownOption => !!reg),
        distinctUntilChanged((a: NgxDropdownOption, b: NgxDropdownOption) => a.id === b.id)
      )
      .subscribe((reg: NgxDropdownOption) => {
        this.selectedRegistration(reg);
        this.filtersStateService.setApplyButtonChanging();
      });

    this.selectedFromWeekPickerFormControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.updatePendingFilters())
      )
      .subscribe((v) => {
        this.onDatePickerChange(v);
        this.filtersStateService.setApplyButtonChanging();
      });

    this.selectedFromDayPickerFormControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.updatePendingFilters())
      )
      .subscribe((v) => {
        this.onDatePickerChange(v);
        this.filtersStateService.setApplyButtonChanging();
      });
  }

  private selectedRegistration(selectedOption: NgxDropdownOption): void {
    this.updatePendingFilters();

    if (this.isFirstTime) {
      this.apply();
    }

    this.isFirstTime = false;
    if (this.regFirstTimeSelected && selectedOption) {
      this.mixPanelService.selectRegistration(selectedOption.value);
    }
    this.regFirstTimeSelected = true;
  }

  private updatePendingFilters(): void {
    const currentReg = this.selectedRegFormControl.value;
    const isGraphView = this.datePickerType() === DatePickerEnum.DatePicker;

    const currentDateRange = isGraphView ? this.selectedFromDayPickerFormControl.value : this.selectedFromWeekPickerFormControl.value;
    if (currentReg?.id && currentDateRange?.start && currentDateRange?.end) {
      this.filtersStateService.setPendingFilters({
        registrationId: currentReg.id,
        dateRangeStart: currentDateRange.start.toISOString(),
        dateRangeEnd: currentDateRange.end.toISOString(),
        datePickerType: this.datePickerType()
      });
    }
  }

  private onDatePickerChange(selectedDate: NgxDateRangePickerOutput | null): void {
    if (selectedDate === null) return;

    if (this.periodSelected && selectedDate) {
      const selectedDateRange =
        this.datePickerType() === DatePickerEnum.WeekPicker
          ? `${selectedDate.start?.format('MM/DD/YYYY')} - ${selectedDate.end?.format('MM/DD/YYYY')}`
          : `${selectedDate.start?.format('MM/DD/YYYY')}`;

      this.mixPanelService.selectDateRange(selectedDateRange);
      this.periodSelected = false;
    } else {
      this.periodSelected = true;
    }

    this.internalState.setSelectedFilterDates(selectedDate);
  }

  protected apply(): void {
    this.filtersStateService.applyFilters();
    this.store.dispatch(Actions.loadRegViewConfig({ registrationId: this.selectedRegFormControl.value.id }));
    const currentRoute = this.route.url.split('?')[0];
    this.route.navigate([currentRoute]);
  }

  private resolveDayPickerOffset(regConfig: RegConfig): number {
    const gateClosesToRelative = regConfig.gateRules?.gate_closes_to_relative;

    if (!gateClosesToRelative) {
      return 0;
    }

    const MINUTES_PER_DAY = 1440;
    return Math.ceil(gateClosesToRelative / MINUTES_PER_DAY);
  }

  private onRegConfigOrRouteChange(regConfig: RegConfig, currentRoute: string): void {
    const isOpenOrCleared = currentRoute.includes('/open') || currentRoute.includes('/cleared');
    const hasGraphView = regConfig.graph_view === true;
    const shouldUseDayPicker = isOpenOrCleared && hasGraphView;

    const previousType = this.datePickerType();
    const newType = shouldUseDayPicker ? DatePickerEnum.DatePicker : DatePickerEnum.WeekPicker;
    this.filtersStateService.setDatePickerType(newType);

    const currentRegId = this.selectedRegFormControl.value?.id ?? null;
    const regConfigChanged = currentRegId !== this.lastRegConfigRegId;

    if (shouldUseDayPicker) {
      const enteringDayPicker = this.isFirstTime || previousType !== DatePickerEnum.DatePicker;

      if (enteringDayPicker || regConfigChanged) {
        const offset = this.resolveDayPickerOffset(regConfig);
        const futureDate = moment().add(offset, 'days');
        this.selectedFromDayPickerFormControl.setValue({ start: futureDate, end: futureDate });
        this.updatePendingFilters();
        this.filtersStateService.applyFilters();
      }
    } else {
      if (this.isFirstTime || previousType !== DatePickerEnum.WeekPicker) {
        if (!this.selectedFromWeekPickerFormControl.value) {
          const startOfWeek = moment().startOf('week');
          const endOfWeek = moment().endOf('week');
          this.selectedFromWeekPickerFormControl.setValue({
            start: startOfWeek,
            end: endOfWeek
          });
        }
        this.selectedFromDayPickerFormControl.setValue(null, { emitEvent: false });
        this.updatePendingFilters();
        this.filtersStateService.applyFilters();
      }
    }

    this.lastRegConfigRegId = currentRegId;
    this.updatePendingFilters();
  }
}
